import type { HotZoneCategory } from "@/types/gdelt";

type Rgb = readonly [number, number, number];
type Rgba = readonly [number, number, number, number];

export type GlobeRenderSettings = {
  sceneClear: Rgba;
  lights: {
    hemisphereIntensity: number;
    keyIntensity: number;
    rimIntensity: number;
    hemisphereColor: Rgb;
    hemisphereGroundColor: Rgb;
    keyColor: Rgb;
    rimColor: Rgb;
  };
  earth: {
    outlineDiffuse: Rgb;
    outlineEmissive: Rgb;
    terrainEmissive: Rgb;
    specular: Rgb;
    ambient: Rgb;
    outlineAlpha: number;
    gridColor: Rgb;
  };
  atmosphere: {
    emissive: Rgb;
    diffuse: Rgb;
    alpha: number;
  };
  boundaries: {
    coreColor: Rgb;
    haloColor: Rgb;
    coreAlpha: number;
    haloAlpha: number;
  };
  signals: Record<HotZoneCategory, { color: Rgb; glow: string }>;
  signalGlowIntensity: number;
  signalSizeMultiplier: number;
};

export const BASELINE_GLOBE_RENDER_SETTINGS: GlobeRenderSettings = {
  sceneClear: [0, 0, 0, 0],
  lights: {
    hemisphereIntensity: 0.48,
    keyIntensity: 1.35,
    rimIntensity: 0.82,
    hemisphereColor: [0.45, 0.78, 1],
    hemisphereGroundColor: [0.02, 0.03, 0.06],
    keyColor: [0.9, 0.95, 1],
    rimColor: [0.05, 0.56, 1],
  },
  earth: {
    outlineDiffuse: [0.01, 0.045, 0.075],
    outlineEmissive: [0.01, 0.09, 0.15],
    terrainEmissive: [0.02, 0.06, 0.08],
    specular: [0.06, 0.16, 0.2],
    ambient: [0.02, 0.06, 0.09],
    outlineAlpha: 0.78,
    gridColor: [0.1, 0.62, 1],
  },
  atmosphere: { emissive: [0.08, 0.55, 1], diffuse: [0.02, 0.1, 0.18], alpha: 0.1 },
  boundaries: { coreColor: [0.34, 0.92, 1], haloColor: [0.05, 0.46, 1], coreAlpha: 0.94, haloAlpha: 0.38 },
  signals: {
    social_unrest: { color: [0.85, 0.19, 0.19], glow: "#d93636" },
    crime: { color: [0.92, 0.72, 0.24], glow: "#eab83d" },
    corruption: { color: [0.08, 0.72, 0.64], glow: "#14b8a6" },
    political_violence: { color: [0.95, 0.24, 0.35], glow: "#f43f5e" },
    institutional_instability: { color: [0.22, 0.74, 0.97], glow: "#38bdf8" },
  },
  signalGlowIntensity: 0.28,
  signalSizeMultiplier: 1,
};

export const SANDBOX_GLOBE_RENDER_SETTINGS: GlobeRenderSettings = {
  ...BASELINE_GLOBE_RENDER_SETTINGS,
  lights: {
    ...BASELINE_GLOBE_RENDER_SETTINGS.lights,
    hemisphereIntensity: 0.3,
    keyIntensity: 0.78,
    rimIntensity: 0.34,
    hemisphereColor: [0.3, 0.54, 0.72],
    rimColor: [0.02, 0.32, 0.58],
  },
  earth: {
    ...BASELINE_GLOBE_RENDER_SETTINGS.earth,
    outlineDiffuse: [0.006, 0.026, 0.045],
    outlineEmissive: [0.006, 0.052, 0.085],
    outlineAlpha: .1,
    gridColor: [0.15,0.15,0.15],
  },
  atmosphere: { emissive: [0.025, 0.22, 0.42], diffuse: [0.008, 0.035, 0.07], alpha: 0.045 },
  boundaries: { coreColor: [0.2, 0.65, 0.85], haloColor: [0.02, 0.2, 0.42], coreAlpha: 0.2, haloAlpha: 0.16 },
  signals: {
    social_unrest: { color: [1, 0.04, 0.025], glow: "#ff100a" },
    crime: { color: [1, 0.82, 0.015], glow: "#ffd105" },
    corruption: { color: [0.025, 0.17, 0.9], glow: "#062be6" },
    political_violence: { color: [0.95, 0.015, 0.3], glow: "#f2044d" },
    institutional_instability: { color: [0.015, 0.7, 0.82], glow: "#04b3d1" },
  },
  signalGlowIntensity: 0.34,
  signalSizeMultiplier: 5.2,
};
